import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import client from '../../../lib/feathers';

/**
 * The reverse of CatalogIncludes (P4): "Listings displaying your wines."
 * Shows every listing that includes THIS listing's catalog (this listing is the
 * source). The source's manager can BLOCK a host (status='blocked') to opt out
 * of being displayed there, or unblock to allow it again. Read-only list of who
 * pours your wines + a one-click opt-out.
 */
export default function DisplayedByListings() {
  const { lid } = useParams();
  const role = useSelector((state) => state.auth.user.role);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const svc = useCallback(() => client.service(`${role}/listing-catalog-include`), [role]);

  const load = useCallback(async () => {
    try {
      const res = await svc().find({ query: { includedListingId: lid, $limit: 100 } });
      setRows(res.data || res || []);
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load listings displaying your wines');
    }
  }, [svc, lid]);

  useEffect(() => { if (role && lid) load(); }, [role, lid, load]);

  const setStatus = async (row, status) => {
    const verb = status === 'blocked' ? 'block' : 'unblock';
    const who = row.listing?.name || 'this listing';
    if (!window.confirm(`${status === 'blocked' ? 'Block' : 'Unblock'} "${who}" from displaying your wines?`)) return;
    setBusy(true);
    try {
      await svc().patch(row.id, { status });
      await load();
    } catch (err) {
      setError(err.message || `Failed to ${verb} (you must manage this listing)`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white shadow px-4 py-5 sm:rounded-lg my-5">
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Listings displaying your wines</h3>
          <p className="mt-1 text-sm text-gray-500">
            Other listings that show this listing&apos;s catalog (wine bars, tasting collectives,
            sister locations). You can <strong>block</strong> any of them to opt out of being
            displayed there.
          </p>
        </div>
        <div className="mt-5 md:mt-0 md:col-span-2">
          {error && <div className="mb-2 text-sm text-red-700">{error}</div>}

          <table className="min-w-full divide-y divide-gray-300">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-900">Displaying listing</th>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-900">Status</th>
                <th className="px-3 py-2"><span className="sr-only">Block</span></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="px-3 py-3 text-sm text-gray-700">
                    {row.listing ? (
                      <a href={`/admin/listings/${row.listing.id}`} className="text-indigo-700 hover:underline">
                        {row.listing.name}
                      </a>
                    ) : <span className="text-gray-400 italic">{row.listingId}</span>}
                    {row.isBrandSource && <span className="ml-2 text-xs text-amber-700">(brand source)</span>}
                  </td>
                  <td className="px-3 py-3 text-sm">
                    {row.status === 'blocked'
                      ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">blocked</span>
                      : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">active</span>}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {row.status === 'blocked' ? (
                      <button type="button" disabled={busy} onClick={() => setStatus(row, 'active')}
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-400 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50">
                        <FontAwesomeIcon icon={faRotateLeft} /> Unblock
                      </button>
                    ) : (
                      <button type="button" disabled={busy} onClick={() => setStatus(row, 'blocked')}
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-red-500 rounded-md text-sm font-medium text-red-600 hover:bg-red-500 hover:text-white disabled:opacity-50">
                        <FontAwesomeIcon icon={faBan} /> Block
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={3} className="p-3 text-sm text-gray-400 italic">No other listings display your wines.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
